// Event browser — visual list of all events (active + closed) so users can
// quickly see what's available, scan the totals, and click one to load its
// report. Solves the "nanti eventnya numpuk dan susah diliat" problem: the
// dropdown is fine for 5 events, but breaks down past 20.
//
// Active events are pinned to the top with a primary highlight; closed events
// are grouped below with neutral styling. A status filter (All / Active /
// Closed) lets the user narrow down.

import { useMemo, useState } from 'react';
import { CalendarRange, History, BarChart3 } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Chip } from '@/components/ui/Chip';
import { cn, formatIDR, formatNumber, formatDate } from '@/lib/format';
import type { EventListItem } from '@/lib/api/reports';

export type EventStatusFilter = 'all' | 'active' | 'closed';

export interface EventBrowserProps {
  events: EventListItem[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}

const FILTER_TABS: Array<{ value: EventStatusFilter; label: string; icon: React.ReactNode }> = [
  { value: 'all', label: 'Semua', icon: <BarChart3 size={14} /> },
  { value: 'active', label: 'Aktif', icon: <CalendarRange size={14} /> },
  { value: 'closed', label: 'History', icon: <History size={14} /> },
];

export function EventBrowser({ events, selectedId, onSelect }: EventBrowserProps) {
  const [filter, setFilter] = useState<EventStatusFilter>('all');

  // Counts per filter (computed from full list, not the filtered subset, so the
  // tab labels stay stable as the user toggles).
  const counts = useMemo(() => {
    return {
      all: events.length,
      active: events.filter((e) => e.status === 'active').length,
      closed: events.filter((e) => e.status === 'closed').length,
    };
  }, [events]);

  // Filtered list, active first within each group, then sorted by startAt desc.
  const visible = useMemo(() => {
    const filtered =
      filter === 'all' ? events : events.filter((e) => e.status === filter);
    return [...filtered].sort((a, b) => {
      // Active before closed
      if (a.status !== b.status) return a.status === 'active' ? -1 : 1;
      // Newest first within group
      return b.startAt - a.startAt;
    });
  }, [events, filter]);

  return (
    <Card padding="none">
      <div className="p-6 pb-4 border-b border-line">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h3 className="text-subhead font-display font-semibold text-ink">
              Pilih event
            </h3>
            <p className="text-caption text-ink-subtle mt-1">
              {events.length === 0
                ? 'Belum ada event sama sekali.'
                : `${formatNumber(events.length)} event total — ${formatNumber(counts.active)} aktif, ${formatNumber(counts.closed)} history.`}
            </p>
          </div>
        </div>
        <div
          role="tablist"
          aria-label="Filter event berdasarkan status"
          className="inline-flex p-1 rounded bg-canvas border border-line"
        >
          {FILTER_TABS.map((t) => (
            <button
              key={t.value}
              type="button"
              role="tab"
              aria-selected={filter === t.value}
              onClick={() => setFilter(t.value)}
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-small font-medium transition-colors',
                filter === t.value
                  ? 'bg-surface text-ink shadow-hover'
                  : 'text-ink-subtle hover:text-ink',
              )}
            >
              {t.icon}
              {t.label} ({formatNumber(counts[t.value])})
            </button>
          ))}
        </div>
      </div>

      {visible.length === 0 ? (
        <div className="px-6 py-10 text-center text-ink-subtle text-small">
          {events.length === 0
            ? 'Mulai event pertama dari menu Events.'
            : `Tidak ada event dengan status "${filter}".`}
        </div>
      ) : (
        <ul className="divide-y divide-line max-h-[420px] overflow-y-auto overscroll-contain">
          {visible.map((evt) => {
            const isSelected = evt.id === selectedId;
            const isActive = evt.status === 'active';
            return (
              <li key={evt.id}>
                <button
                  type="button"
                  onClick={() => onSelect(evt.id)}
                  aria-pressed={isSelected}
                  className={cn(
                    'w-full text-left px-6 py-4 flex items-center gap-4 transition-colors',
                    'hover:bg-canvas/50 focus:bg-canvas/50 focus:outline-none',
                    isSelected && 'bg-primary/5 hover:bg-primary/10',
                  )}
                >
                  {/* Status indicator dot */}
                  <span
                    className={cn(
                      'w-2.5 h-2.5 rounded-full shrink-0',
                      isActive ? 'bg-primary animate-pulse' : 'bg-ink-subtle/40',
                    )}
                    aria-hidden
                  />

                  {/* Name + meta */}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p
                        className={cn(
                          'text-body font-medium truncate',
                          isSelected ? 'text-primary' : 'text-ink',
                        )}
                      >
                        {evt.name}
                      </p>
                      <Chip variant={isActive ? 'active' : 'default'}>
                        {isActive ? 'Aktif' : 'Closed'}
                      </Chip>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-caption text-ink-subtle">
                      <code className="font-mono">{evt.code}</code>
                      <span aria-hidden>·</span>
                      <span>{formatDate(evt.startAt)}</span>
                      {evt.endAt ? (
                        <>
                          <span aria-hidden>→</span>
                          <span>{formatDate(evt.endAt)}</span>
                        </>
                      ) : null}
                    </div>
                  </div>

                  {/* Totals */}
                  <div className="text-right shrink-0 hidden sm:block">
                    <p className="text-body font-display font-semibold text-ink tabular-nums">
                      {formatIDR(evt.totalSales)}
                    </p>
                    <p className="text-caption text-ink-subtle tabular-nums">
                      {formatNumber(evt.totalTransactions)} transaksi
                    </p>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
